import { useState, useEffect, useRef } from 'react';

// VU Meter Component
export const VUMeter = ({ value = 0, max = 100, color = '#00ff9f', label = '' }) => {
  const bars = 20;
  const filledBars = Math.floor((value / max) * bars);

  const getBarColor = (index) => {
    if (index < filledBars * 0.6) return '#00ff9f'; // Green
    if (index < filledBars * 0.8) return '#ffff00'; // Yellow
    return '#ff00ff'; // Magenta
  };

  return (
    <div className="flex flex-col space-y-1">
      {label && (
        <div className="text-xs text-cyan-400 font-mono uppercase tracking-wider">{label}</div>
      )}
      <div className="flex items-end space-x-1">
        {Array.from({ length: bars }).map((_, i) => (
          <div
            key={i}
            className={`led ${i < filledBars ? 'active' : ''}`}
            style={{
              background: i < filledBars ? getBarColor(i) : 'rgba(0, 255, 159, 0.1)',
              color: getBarColor(i),
              height: `${10 + (i / bars) * 30}px`,
            }}
          />
        ))}
      </div>
      <div className="text-xs text-right font-mono neon-cyan">
        {value.toFixed(1)}/{max}
      </div>
    </div>
  );
};

// Circular Gauge
export const CircularGauge = ({ value = 0, max = 100, label = '', unit = '', color = '#00ff9f' }) => {
  const percentage = (value / max) * 100;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg className="transform -rotate-90" width="120" height="120">
        {/* Background circle */}
        <circle
          cx="60"
          cy="60"
          r="45"
          stroke="rgba(0, 255, 159, 0.1)"
          strokeWidth="8"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="60"
          cy="60"
          r="45"
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.5s ease',
            filter: `drop-shadow(0 0 8px ${color})`,
          }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center">
        <div className="text-2xl font-bold" style={{ color, textShadow: `0 0 10px ${color}` }}>
          {value.toFixed(0)}
        </div>
        <div className="text-xs" style={{ color }}>
          {unit}
        </div>
      </div>
      {label && (
        <div className="absolute -bottom-6 text-xs text-cyan-400 font-mono uppercase">
          {label}
        </div>
      )}
    </div>
  );
};

// Console Log Component
export const ConsoleLog = ({ logs = [], maxLogs = 10 }) => {
  const [displayLogs, setDisplayLogs] = useState(logs);
  const logEndRef = useRef(null);

  useEffect(() => {
    setDisplayLogs((prev) => [...prev, ...logs].slice(-maxLogs));
  }, [logs, maxLogs]);

  useEffect(() => {
    // Scroll only within the console, not the entire page
    if (logEndRef.current) {
      const container = logEndRef.current.parentElement;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [displayLogs]);

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return '#ff00ff';
      case 'warning': return '#ffff00';
      case 'success': return '#00ff9f';
      case 'info': return '#00f0ff';
      default: return '#00ff9f';
    }
  };

  return (
    <div className="console-log">
      {displayLogs.map((log, index) => (
        <div
          key={index}
          className="mb-1"
          style={{ color: getLogColor(log.type) }}
        >
          <span className="opacity-60">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
          <span className="ml-2">&gt;</span>
          <span className="ml-2">{log.message}</span>
        </div>
      ))}
      <div ref={logEndRef} />
    </div>
  );
};

// Status LED
export const StatusLED = ({ status = 'online', label = '', pulse = true }) => {
  const colors = {
    online: '#00ff9f',
    offline: '#666',
    warning: '#ffff00',
    error: '#ff00ff',
  };

  return (
    <div className="flex items-center space-x-2">
      <div
        className={`w-3 h-3 rounded-full ${pulse ? 'pulse-neon' : ''}`}
        style={{
          background: colors[status],
          boxShadow: `0 0 10px ${colors[status]}`,
        }}
      />
      {label && (
        <span className="text-xs font-mono uppercase" style={{ color: colors[status] }}>
          {label}
        </span>
      )}
    </div>
  );
};

// Data Stream Bar
export const DataStreamBar = ({ data = [], label = '' }) => {
  return (
    <div className="w-full">
      {label && (
        <div className="text-xs text-cyan-400 font-mono uppercase mb-1">{label}</div>
      )}
      <div className="flex items-center space-x-1 h-8 bg-black bg-opacity-50 border border-cyan-400 p-1">
        {data.slice(-30).map((value, index) => (
          <div
            key={index}
            className="flex-1 bg-cyan-400"
            style={{
              height: `${(value / 100) * 100}%`,
              boxShadow: '0 0 5px #00f0ff',
              transition: 'height 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Cyber Button
export const CyberButton = ({ children, onClick, variant = 'primary', disabled = false, protected: isProtected = false, ...props }) => {
  const variants = {
    primary: 'border-[#00ff9f] text-[#00ff9f] hover:bg-[#00ff9f] hover:bg-opacity-10',
    secondary: 'border-[#00f0ff] text-[#00f0ff] hover:bg-[#00f0ff] hover:bg-opacity-10',
    danger: 'border-[#ff00ff] text-[#ff00ff] hover:bg-[#ff00ff] hover:bg-opacity-10',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`cyber-button ${variants[variant]} disabled:opacity-50 disabled:cursor-not-allowed`}
      {...props}
    >
      {children}
    </button>
  );
};

// Holographic Card
export const HoloCard = ({ children, className = '' }) => {
  return (
    <div className={`cyber-card p-6 hologram ${className}`}>
      {children}
    </div>
  );
};

// System Monitor
export const SystemMonitor = ({ cpu = 0, ram = 0, network = 0 }) => {
  return (
    <div className="grid grid-cols-3 gap-4 p-4 bg-black bg-opacity-50 border border-cyan-400">
      <div>
        <div className="text-xs text-cyan-400 font-mono mb-1">CPU</div>
        <VUMeter value={cpu} max={100} color="#00ff9f" />
      </div>
      <div>
        <div className="text-xs text-cyan-400 font-mono mb-1">RAM</div>
        <VUMeter value={ram} max={100} color="#ffff00" />
      </div>
      <div>
        <div className="text-xs text-cyan-400 font-mono mb-1">NET</div>
        <VUMeter value={network} max={100} color="#ff00ff" />
      </div>
    </div>
  );
};